import { type Color, KING, type Position, TYPE_MASK, WHITE, baseType, makePiece, pieceColor, pieceDef } from '@chessbut/engine';
import { Flame } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatClock } from '../lib/format.ts';
import { Link } from '../lib/router.tsx';
import { type PlayerNote, pieceImage } from '../variants/ui.ts';

const points = (type: number) => (type === KING ? 0 : Math.round(pieceDef(type).value / 100));

/** Pieces per side by kind (a wounded or frozen piece still counts as what it is). */
function counts(position: Position) {
  const c = [new Map<number, number>(), new Map<number, number>()];
  for (const p of position.board) {
    const color = pieceColor(p);
    if (color === -1) continue;
    const t = baseType(p & TYPE_MASK);
    c[color].set(t, (c[color].get(t) ?? 0) + 1);
  }
  return c;
}

/** Pieces each side has lost compared with the start, and the material balance (White +). */
export function materialSummary(position: Position, start: Position) {
  const now = counts(position);
  const before = counts(start);
  const missing: number[][] = [[], []];
  for (const c of [0, 1] as Color[]) {
    const types = [...new Set([...before[c].keys(), ...now[c].keys()])].sort((a, b) => points(b) - points(a));
    for (const t of types) {
      for (let i = now[c].get(t) ?? 0; i < (before[c].get(t) ?? 0); i++) missing[c].push(makePiece(c, t));
    }
  }
  let balance = 0;
  for (const p of position.board) {
    const color = pieceColor(p);
    if (color !== -1) balance += (color === 0 ? 1 : -1) * points(p & TYPE_MASK);
  }
  return { missing, balance };
}

export function Clock({ ms, running, at }: { ms: number; running: boolean; at: number }) {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(performance.now()), 100);
    return () => clearInterval(id);
  }, [running]);
  const left = running ? ms - (now - at) : ms;
  return <div className={`clock ${running ? 'is-running' : ''} ${left < 10_000 && running ? 'is-low' : ''}`}>{formatClock(left)}</div>;
}

export interface PlayerBarProps {
  color: Color;
  name: string;
  username?: string | null;
  /** e.g. "1532" or "1500?" */
  rating?: string | null;
  isYou?: boolean;
  bot?: boolean;
  offline?: boolean;
  active: boolean;
  position: Position;
  start: Position;
  note?: PlayerNote | null;
  streak?: number;
  clock?: { ms: number; running: boolean; at: number } | null;
  /** Leave out captured pieces and the material lead (they'd give hidden pieces away). */
  hideMaterial?: boolean;
}

export function PlayerBar(props: PlayerBarProps) {
  const { missing, balance } = materialSummary(props.position, props.start);
  const theirMissing = props.hideMaterial ? [] : missing[props.color === WHITE ? 1 : 0];
  const lead = props.hideMaterial ? 0 : props.color === WHITE ? balance : -balance;
  const name = props.username ? (
    <Link to={`/u/${props.username}`} className="player-link">
      {props.name}
    </Link>
  ) : (
    <span className="player-link">{props.name}</span>
  );
  return (
    <div className={`player ${props.active ? 'is-active' : ''}`}>
      <span className={`player-piece ${props.color === WHITE ? 'w' : 'b'}`}>
        <img src={pieceImage(makePiece(props.color, KING))} alt={props.color === WHITE ? 'White' : 'Black'} />
      </span>
      <div className="who">
        <div className="name">
          {name}
          {props.isYou && <span className="you-tag">you</span>}
          {props.bot && <span className="you-tag">computer</span>}
          {props.streak ? (
            <span className="player-streak" title={`${props.streak}-day streak`}>
              <Flame className="icon sm" aria-hidden />
              <span className="num">{props.streak}</span>
            </span>
          ) : null}
          {props.offline && <span className="offline-tag">offline</span>}
        </div>
        <div className="sub">
          {props.rating && <span className="rating">{props.rating}</span>}
          {props.note && (
            <span className={`note ${props.note.tone ?? ''}`} title={props.note.title}>
              {props.note.icon && <img src={props.note.icon} alt="" />}
              {props.note.text}
            </span>
          )}
          <span className="captured">
            {theirMissing.map((p, i) => (
              <img key={i} src={pieceImage(p)} alt="" />
            ))}
            {lead > 0 && <span className="lead">+{lead}</span>}
          </span>
        </div>
      </div>
      {props.clock && <Clock {...props.clock} />}
    </div>
  );
}
